import { getActiveAccount } from './util.auth.getActiveAccount';
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  PopupRequest,
  SilentRequest,
} from '@azure/msal-browser';

import { loginRequest } from '@frontend/features/auth/config/authConfig';
import {
  ensureMsalInitialized,
  msalInstance,
} from '@frontend/features/auth/config/msalInstance';
import { getAuthMode } from '@frontend/features/auth/util/util.auth.getAuthMode';
import { getLocalToken } from '@frontend/features/auth/util/util.auth.localToken';

/**
 * Acquires an authentication token based on current authentication mode
 *
 * **E2E Mode** (token in localStorage):
 * - Returns token from localStorage
 * - Works on all environments (localhost, dev, qa, prod)
 * - Used by Playwright E2E tests and localhost development
 *
 * **MSAL Mode** (no token in localStorage):
 * - Attempts silent token acquisition from MSAL
 * - Falls back to popup authentication if needed
 * - Falls back to redirect authentication if popup fails
 *
 * @returns Authentication token
 * @throws Error if no token available or authentication fails
 */
export async function acquireToken(): Promise<string> {
  const authMode = getAuthMode();

  // E2E mode: use token from localStorage
  if (authMode === 'e2e') {
    const localToken = getLocalToken();
    if (!localToken) {
      throw new Error(
        'E2E mode active but no token found. Please paste a token or visit /change-to-e2e',
      );
    }
    console.log('[AUTH] Using E2E token from localStorage');
    return localToken;
  }

  // MSAL mode: use Azure AD authentication
  await ensureMsalInitialized();

  const account = await getActiveAccount();

  if (!account) {
    throw new Error('No active account');
  }

  const request: SilentRequest = {
    ...loginRequest,
    account,
  };

  try {
    // Attempt silent token acquisition
    console.log('Attempting silent token acquisition...');
    const response = await msalInstance.acquireTokenSilent(request);
    console.log('Silent token acquisition successful');
    return response.accessToken;
  } catch (error) {
    console.warn('Silent token acquisition failed:', error);

    // Handle interaction required (user session expired/missing)
    if (error instanceof InteractionRequiredAuthError) {
      console.log('Interaction required - attempting popup authentication');
      return await handleInteractionRequired(request);
    }

    // Handle browser errors (timeout, popup blocked, etc.)
    if (error instanceof BrowserAuthError) {
      // MSAL v5: Handle both timeout error codes
      if (
        error.errorCode === 'monitor_window_timeout' ||
        error.errorCode === 'timed_out'
      ) {
        console.log('Silent token timeout - attempting popup authentication');
        return await handleInteractionRequired(request);
      }
    }

    // Re-throw unexpected errors
    throw error;
  }
}

async function handleInteractionRequired(
  request: PopupRequest,
): Promise<string> {
  try {
    // Try popup first (better UX, preserves app state)
    console.log('Attempting token acquisition via popup...');
    const response = await msalInstance.acquireTokenPopup(request);
    console.log('Popup token acquisition successful');
    return response.accessToken;
  } catch (popupError) {
    console.warn('Popup token acquisition failed:', popupError);

    // Fallback to redirect (more reliable, but disrupts app state)
    if (popupError instanceof BrowserAuthError) {
      console.log('Popup blocked or failed - redirecting to login...');

      // Save current path before redirect so we can return here after auth
      sessionStorage.setItem(
        '__MSAL_REDIRECT_RETURN_PATH__',
        window.location.pathname,
      );

      await msalInstance.acquireTokenRedirect(request);

      // acquireTokenRedirect never returns (redirects page)
      // Throw error to prevent further execution
      throw new Error('Redirecting to login page...');
    }

    throw popupError;
  }
}
